import React, { useState, useEffect } from 'react';
import ConfirmModal from '../components/common/ConfirmModal';
import { Link, useNavigate } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import MainLayout from '../components/layout/MainLayout';
import { useNotification } from '../contexts/NotificationContext';
import {
  useGetCampaignsQuery,
  useCreateCampaignMutation,
  useUpdateCampaignMutation,
  useDeleteCampaignMutation,
  useScheduleCampaignMutation,
  usePauseCampaignMutation,
  useResumeCampaignMutation,
  useGetTemplatesQuery,
  useGetContactsQuery,
  useGetContactGroupsQuery
} from '../store/services/whatsappApi';

// WhatsApp pricing data by country code
const WHATSAPP_PRICING: { [key: string]: number } = {
  // North America
  '1': 0.0085,    // USA, Canada

  // Western Europe
  '44': 0.0085,   // UK
  '33': 0.0085,   // France
  '49': 0.0085,   // Germany
  '39': 0.0085,   // Italy
  '34': 0.0085,   // Spain

  // Brazil
  '55': 0.0085,

  // India
  '91': 0.005,

  // Africa
  '254': 0.005,   // Kenya
  '255': 0.005,   // Tanzania
  '256': 0.005,   // Uganda
  '27': 0.005,    // South Africa
  '233': 0.005,   // Ghana
  '234': 0.005,   // Nigeria
  '20': 0.020,    // Egypt

  // Asia Pacific
  '65': 0.005,    // Singapore
  '60': 0.005,    // Malaysia
  '62': 0.005,    // Indonesia
  '66': 0.005,    // Thailand
  '84': 0.005,    // Vietnam
  '63': 0.015,    // Philippines
  '82': 0.015,    // South Korea
  '81': 0.015,    // Japan

  // Default for other countries
  'default': 0.0085
};

// Extract country code from phone number
const extractCountryCode = (phoneNumber: string): string => {
  // Remove any non-numeric characters except +
  const cleaned = phoneNumber.replace(/[^\d+]/g, '');

  // Handle international format (+country code)
  if (cleaned.startsWith('+')) {
    // Common country codes (1-3 digits)
    if (cleaned.match(/^\+1/)) return '1';      // North America
    if (cleaned.match(/^\+44/)) return '44';    // UK
    if (cleaned.match(/^\+33/)) return '33';    // France
    if (cleaned.match(/^\+49/)) return '49';    // Germany
    if (cleaned.match(/^\+39/)) return '39';    // Italy
    if (cleaned.match(/^\+34/)) return '34';    // Spain
    if (cleaned.match(/^\+55/)) return '55';    // Brazil
    if (cleaned.match(/^\+91/)) return '91';    // India
    if (cleaned.match(/^\+254/)) return '254';  // Kenya
    if (cleaned.match(/^\+255/)) return '255';  // Tanzania
    if (cleaned.match(/^\+256/)) return '256';  // Uganda
    if (cleaned.match(/^\+27/)) return '27';    // South Africa
    if (cleaned.match(/^\+233/)) return '233';  // Ghana
    if (cleaned.match(/^\+234/)) return '234';  // Nigeria
    if (cleaned.match(/^\+20/)) return '20';    // Egypt
    if (cleaned.match(/^\+65/)) return '65';    // Singapore
    if (cleaned.match(/^\+60/)) return '60';    // Malaysia
    if (cleaned.match(/^\+62/)) return '62';    // Indonesia
    if (cleaned.match(/^\+66/)) return '66';    // Thailand
    if (cleaned.match(/^\+84/)) return '84';    // Vietnam
    if (cleaned.match(/^\+63/)) return '63';    // Philippines
    if (cleaned.match(/^\+82/)) return '82';    // South Korea
    if (cleaned.match(/^\+81/)) return '81';    // Japan
  }

  // If no country code found, return default
  return 'default';
};

// Calculate estimated campaign cost
const calculateCampaignCost = (contactGroupIds: string[], contacts: any[], contactGroups: any[]): { totalCost: number; breakdown: { [key: string]: { count: number; cost: number } } } => {
  const countryCounts: { [key: string]: number } = {};

  // Process selected contacts and groups
  contactGroupIds.forEach(id => {
    if (id.startsWith('contact:')) {
      const contactId = id.split(':')[1];
      const contact = contacts?.find(c => c.id === parseInt(contactId));
      if (contact) {
        const countryCode = extractCountryCode(contact.phone_number);
        countryCounts[countryCode] = (countryCounts[countryCode] || 0) + 1;
      }
    } else if (id.startsWith('group:')) {
      const groupId = id.split(':')[1];
      const group = contactGroups?.find(g => g.id === parseInt(groupId));
      if (group) {
        // For groups, we'd need to get individual contacts, but for estimation we'll use a representative sample
        // For now, assume average distribution or use group location if available
        const estimatedCountry = 'default'; // Could be enhanced to use group metadata
        const memberCount = group.memberCount || 1;
        countryCounts[estimatedCountry] = (countryCounts[estimatedCountry] || 0) + memberCount;
      }
    }
  });

  // Calculate costs
  const breakdown: { [key: string]: { count: number; cost: number } } = {};
  let totalCost = 0;

  Object.entries(countryCounts).forEach(([countryCode, count]) => {
    const costPerConversation = WHATSAPP_PRICING[countryCode] || WHATSAPP_PRICING.default;
    const countryCost = count * costPerConversation;
    breakdown[countryCode] = { count, cost: countryCost };
    totalCost += countryCost;
  });



  return { totalCost, breakdown };
};

type CampaignFormData = {
  name: string;
  description: string;
  templateId: string;
  scheduledAt?: string;
  contactGroupIds: string[];
  status?: string;
  
  // Recurring campaign fields
  isRecurring?: number; // 0: one-time, 1: recurring
  recurrenceType?: string; // daily, weekly, monthly
  recurrenceDays?: number[]; // Days for weekly (0-6) or monthly (1-31)
  recurrenceHour?: number; // Hour of day (0-23)
  recurrenceMinute?: number; // Minute (0-59)
  recurrenceEndDate?: string; // When to stop recurring campaigns
};

const Campaigns: React.FC = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentCampaign, setCurrentCampaign] = useState<any>(null);
  
  // New state for improved recipient selection
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedTab, setSelectedTab] = useState('contacts'); // 'contacts' or 'groups'
  const [selectedContacts, setSelectedContacts] = useState<string[]>([]);
  const [selectedGroups, setSelectedGroups] = useState<string[]>([]);
  const [recipientCount, setRecipientCount] = useState(0);

  // Cost estimation state
  const [campaignCost, setCampaignCost] = useState<{ totalCost: number; breakdown: { [key: string]: { count: number; cost: number } } } | null>(null);
  
  // Recurring campaign state
  const [isRecurring, setIsRecurring] = useState(false);
  const [recurrenceType, setRecurrenceType] = useState('daily');
  const [recurrenceDays, setRecurrenceDays] = useState<number[]>([]);
  const [recurrenceHour, setRecurrenceHour] = useState(9); // Default to 9 AM
  const [recurrenceMinute, setRecurrenceMinute] = useState(0);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [showRecurringOptions, setShowRecurringOptions] = useState(false);
  // Delete confirmation modal state
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [campaignToDelete, setCampaignToDelete] = useState<any>(null);
  const [isDeletingConfirm, setIsDeletingConfirm] = useState(false);

  // Sorting state for campaigns list
  const [orderBy, setOrderBy] = useState<string>('created_at');
  const [orderDir, setOrderDir] = useState<'asc' | 'desc'>('desc');

  const { register, handleSubmit, reset, watch, control, formState: { errors } } = useForm<CampaignFormData>();
  const { data: campaigns, isLoading, refetch } = useGetCampaignsQuery({ orderBy, orderDir });
  const { data: templates } = useGetTemplatesQuery({ status: "APPROVED" });
  const { data: contacts } = useGetContactsQuery({ limit: 10000 });
  const { data: contactGroups } = useGetContactGroupsQuery();
  const [createCampaign, { isLoading: isCreating }] = useCreateCampaignMutation();
  const [updateCampaign, { isLoading: isUpdating }] = useUpdateCampaignMutation();
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [deleteCampaign, { isLoading: isDeleting }] = useDeleteCampaignMutation();
  const [scheduleCampaign, { isLoading: isScheduling }] = useScheduleCampaignMutation();
  const [pauseCampaign, { isLoading: isPausing }] = usePauseCampaignMutation();
  const [resumeCampaign, { isLoading: isResuming }] = useResumeCampaignMutation();
  
  // Filter contacts and groups based on search term
  const filteredContacts = contacts?.contacts?.filter(contact =>
    contact.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    contact.phone_number?.toLowerCase().includes(searchTerm.toLowerCase())
  ) || [];
  
  const filteredGroups = contactGroups?.filter((group: any) => 
    group.name?.toLowerCase().includes(searchTerm.toLowerCase())
  ) || [];
  
  // Calculate total recipient count when selections change
  useEffect(() => {
    let count = selectedContacts.length;

    // Add count of contacts in each selected group
    selectedGroups.forEach(groupId => {
      const group = contactGroups?.find((g: any) => g.id === groupId);
      if (group && group.memberCount) {
        count += group.memberCount;
      }
    });

    setRecipientCount(count);
  }, [selectedContacts, selectedGroups, contactGroups]);

  // Calculate campaign cost when selections change
  useEffect(() => {
    if ((selectedContacts.length > 0 || selectedGroups.length > 0) && contacts && contactGroups) {
      const contactGroupIds = [
        ...selectedContacts.map(id => `contact:${id}`),
        ...selectedGroups.map(id => `group:${id}`)
      ];
      const cost = calculateCampaignCost(contactGroupIds, contacts?.contacts || [], contactGroups);
      setCampaignCost(cost);
    } else {
      setCampaignCost(null);
    }
  }, [selectedContacts, selectedGroups, contacts, contactGroups]);
  
  // Synchronize selected contacts and groups with form data
  useEffect(() => {
    const combinedRecipients = [
      ...selectedContacts.map(id => `contact:${id}`),
      ...selectedGroups.map(id => `group:${id}`)
    ];
    
    if (combinedRecipients.length > 0) {
      reset({
        ...watch(),
        contactGroupIds: combinedRecipients
      });
    }
  }, [selectedContacts, selectedGroups, reset, watch]);

  const onSubmit = async (data: CampaignFormData) => {
    try {
      // Format recipient IDs to ensure compatibility with backend
      const formattedContactGroupIds = data.contactGroupIds.map(id => {
        // Ensure all IDs have proper prefix format
        if (id.startsWith('contact:') || id.startsWith('group:')) {
          return id; // Keep existing format
        }
        return `group:${id}`; // Add prefix for legacy IDs
      }).filter(id => id); // Remove any empty IDs

      // Make sure we have the template ID as a number
      const templateId = data.templateId ? parseInt(data.templateId.toString()) : null;

      // Create a data object that can accept any property name for backend
      const backendData: Record<string, any> = {
        // Copy basic fields from form data
        id: currentCampaign?.id,
        name: data.name,
        description: data.description,
        templateId: templateId, // Ensure template ID is sent as number
        contactGroupIds: formattedContactGroupIds,
        status: currentCampaign ? currentCampaign.status : 'draft',
      };
      
      // Handle recurring campaign settings
      if (isRecurring) {
        // Add recurring campaign fields in snake_case for backend
        backendData.is_recurring = 1;
        backendData.recurrence_type = recurrenceType;
        
        // Only include days for weekly and monthly recurrence
        if (recurrenceType === 'weekly' || recurrenceType === 'monthly') {
          backendData.recurrence_days = recurrenceDays.length > 0 ? recurrenceDays : 
            (recurrenceType === 'weekly' ? [1] : [1]); // Default to Monday for weekly or 1st for monthly
        }
        
        // Add time settings
        backendData.recurrence_hour = recurrenceHour;
        backendData.recurrence_minute = recurrenceMinute;
        
        // Add end date if provided
        if (data.recurrenceEndDate) {
          backendData.recurrence_end_date = new Date(data.recurrenceEndDate).toISOString();
        }
      } else {
        // For one-time campaigns, handle scheduledAt with timezone info
        if (data.scheduledAt) {
          // Create date object from input
          const scheduledDate = new Date(data.scheduledAt);
          
          // Add timezone information to the ISO string 
          const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
          backendData.scheduled_at = scheduledDate.toISOString();
          backendData.timezone = userTimezone;
        } else {
          backendData.scheduled_at = undefined;
        }
        
        backendData.is_recurring = 0;
      }
      
      // Log the data being prepared for the backend
      console.log('Preparing backend data:', backendData);

      // Log the data being sent to verify structure
      console.log('Submitting campaign data:', backendData);

      if (currentCampaign) {
        await updateCampaign(backendData).unwrap();
      } else {
        await createCampaign(backendData).unwrap();
      }
      
       // Reset UI state
       setIsModalOpen(false);
       setSelectedContacts([]);
       setSelectedGroups([]);
       setSearchTerm('');
       setCampaignCost(null);
       reset();
       setCurrentCampaign(null);
    } catch (error: any) {
      console.error('Error saving campaign', error);
      showNotification('error', `Failed to save campaign: ${error?.data?.detail || 'Unknown error'}`);
    }
  };

  // Campaign detail page redirection
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const viewCampaignDetails = (campaign: any) => {
    navigate(`/campaign/${campaign.id}/details`);
  };

  const handleDelete = (campaign: any) => {
    setCampaignToDelete(campaign);
    setIsDeleteModalOpen(true);
  };

  const confirmDeleteCampaign = async () => {
    if (!campaignToDelete) return;
    try {
      setIsDeletingConfirm(true);
      const deletedName = campaignToDelete?.name;
      await deleteCampaign(campaignToDelete.id).unwrap();
      setIsDeleteModalOpen(false);
      setCampaignToDelete(null);
      setIsDeletingConfirm(false);
      refetch();
      if (deletedName) {
        showNotification('success', `Campaign "${deletedName}" deleted successfully`);
      }
    } catch (error: any) {
      console.error('Error deleting campaign', error);
      setIsDeletingConfirm(false);
    }
  };

  // Access notification context
  const { showNotification } = useNotification();

  const handleSchedule = async (campaign: any) => {
    try {
      console.log('Scheduling campaign:', campaign.id);
      const result = await scheduleCampaign(campaign.id).unwrap();
      console.log('Campaign scheduled successfully:', result);
      
      // Display success message using notification context
      showNotification('success', result.message || `Campaign "${campaign.name}" scheduled successfully!`);
      
      // Refetch campaigns to update the UI
      refetch();
    } catch (error: any) {
      console.error('Error scheduling campaign:', error);
      showNotification('error', `Failed to schedule campaign: ${error?.data?.detail || 'Unknown error'}`);
    }
  };

  const handlePauseCampaign = async (campaign: any) => {
    try {
      const result = await pauseCampaign(campaign.id).unwrap();
      
      // Show success notification
      showNotification('success', result.message || `Campaign "${campaign.name}" paused successfully`);
      
      // Refresh data
      refetch();
    } catch (error: any) {
      console.error('Error pausing campaign', error);
      showNotification('error', `Failed to pause campaign: ${error?.data?.detail || 'Unknown error'}`);
    }
  };

  const handleResume = async (campaign: any) => {
    try {
      const result = await resumeCampaign(campaign.id).unwrap();
      
      // Show success notification
      showNotification('success', result.message || `Campaign "${campaign.name}" resumed successfully`);
      
      // Refresh data
      refetch();
    } catch (error: any) {
      console.error('Error resuming campaign', error);
      showNotification('error', `Failed to resume campaign: ${error?.data?.detail || 'Unknown error'}`);
    }
  };

  const getCampaignStatusBadge = (status: string) => {
    switch (status) {
      case 'draft':
        return <span className="px-2 py-1 text-xs rounded-full bg-gray-100 text-gray-800">Draft</span>;
      case 'scheduled':
        return <span className="px-2 py-1 text-xs rounded-full bg-blue-100 text-blue-800">Scheduled</span>;
      case 'sending':
        return <span className="px-2 py-1 text-xs rounded-full bg-yellow-100 text-yellow-800">Sending</span>;
      case 'paused':
        return <span className="px-2 py-1 text-xs rounded-full bg-orange-100 text-orange-800">Paused</span>;
      case 'completed':
        return <span className="px-2 py-1 text-xs rounded-full bg-green-100 text-green-800">Completed</span>;
      case 'failed':
        return <span className="px-2 py-1 text-xs rounded-full bg-red-100 text-red-800">Failed</span>;
      default:
        return <span className="px-2 py-1 text-xs rounded-full bg-gray-100 text-gray-800">{status}</span>;
    }
  };

  const navigate = useNavigate();
  
  const openNewCampaignForm = () => {
    navigate('/campaigns/create');
  };
  
  const editCampaign = (campaignId: string | number) => {
    navigate(`/campaigns/edit/${campaignId}`);
  };

  // Helper function to compare IDs that might be strings or numbers
  const idsAreEqual = (id1: string | number, id2: string | number): boolean => {
    return String(id1) === String(id2);
  };

  // Check if contact is selected
  const isContactSelected = (contactId: string | number): boolean => {
    return selectedContacts.some(id => idsAreEqual(id, contactId));
  };

  // Check if group is selected
  const isGroupSelected = (groupId: string | number): boolean => {
    return selectedGroups.some(id => idsAreEqual(id, groupId));
  };
  
  // Toggle contact selection
  const toggleContactSelection = (contactId: string | number) => {
    const stringId = String(contactId);
    if (isContactSelected(contactId)) {
      setSelectedContacts(selectedContacts.filter(id => !idsAreEqual(id, contactId)));
    } else {
      setSelectedContacts([...selectedContacts, stringId]);
    }
  };
  
  // Toggle group selection
  const toggleGroupSelection = (groupId: string | number) => {
    const stringId = String(groupId);
    if (isGroupSelected(groupId)) {
      setSelectedGroups(selectedGroups.filter(id => !idsAreEqual(id, groupId)));
    } else {
      setSelectedGroups([...selectedGroups, stringId]);
    }
  };

  const clearAllSelections = () => {
    setSelectedContacts([]);
    setSelectedGroups([]);
  };
  
  const formatDate = (dateString: string) => {
    const options: Intl.DateTimeFormatOptions = {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  // Calculate campaign metrics
  const getCampaignMetrics = (campaign: any) => {
    // Calculate total contacts from the new contact_details field
    let totalContacts = 0;
    
    // Use the new contact_details field if available
    if (campaign.contact_details && campaign.contact_details.length > 0) {
      // For each contact detail, count contacts based on type
      campaign.contact_details.forEach((detail: any) => {
        if (detail.type === 'contact') {
          // Individual contact counts as 1
          totalContacts += 1;
        } else if (detail.type === 'group') {
          // Add group member count if available, otherwise count as 1
          if (detail.detail && detail.detail.member_count) {
            totalContacts += detail.detail.member_count;
          } else {
            totalContacts += 1; // Fallback if member count is not available
          }
        }
      });
    } else if (campaign.contactGroupIds && campaign.contactGroupIds.length > 0) {
      // Fallback: count each ID as 1 contact if detailed information is not available
      totalContacts = campaign.contactGroupIds.length;
    }
    
    // Legacy fallback (keep this for backward compatibility)
    if (totalContacts === 0 && campaign.contactGroups) {
      totalContacts = campaign.contactGroups?.reduce((acc: number, group: any) => acc + (group.contactCount || 0), 0) || 0;
    }
    
    const sentCount = campaign.stats?.sent || 0;
    const deliveredCount = campaign.stats?.delivered || 0;
    const readCount = campaign.stats?.read || 0;
    const failedCount = campaign.stats?.failed || 0;

    const sentPercentage = totalContacts > 0 ? (sentCount / totalContacts) * 100 : 0;
    const deliveredPercentage = sentCount > 0 ? (deliveredCount / sentCount) * 100 : 0;
    const readPercentage = deliveredCount > 0 ? (readCount / deliveredCount) * 100 : 0;
    const failedPercentage = sentCount > 0 ? (failedCount / sentCount) * 100 : 0;

    return {
      totalContacts,
      sentCount,
      deliveredCount,
      readCount,
      failedCount,
      sentPercentage,
      deliveredPercentage,
      readPercentage,
      failedPercentage
    };
  };

  return (
    <MainLayout>
      <div className="container mx-auto px-4 py-6">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-bold text-whatsapp-teal">WhatsApp Campaigns</h1>
          <button
            onClick={openNewCampaignForm}
            className="bg-whatsapp-green hover:bg-whatsapp-dark-green text-white px-4 py-2 rounded-md flex items-center"
          >
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            New Campaign
          </button>
        </div>

        {/* Sorting controls */}
        <div className="bg-white shadow-sm rounded-md p-4 mb-4 flex items-center gap-4">
          <div className="flex items-center gap-2">
            <label className="text-sm text-gray-600">Sort by</label>
            <select
              value={orderBy}
              onChange={(e) => setOrderBy(e.target.value)}
              className="shadow border rounded py-1.5 px-2 text-sm text-gray-700 focus:outline-none focus:border-whatsapp-green"
            >
              <option value="created_at">Creation date</option>
              <option value="updated_at">Updated date</option>
              <option value="next_run_at">Next run</option>
              <option value="scheduled_at">Scheduled at</option>
              <option value="name">Name</option>
            </select>
          </div>
          <div className="flex items-center gap-2">
            <label className="text-sm text-gray-600">Direction</label>
            <select
              value={orderDir}
              onChange={(e) => setOrderDir(e.target.value as 'asc' | 'desc')}
              className="shadow border rounded py-1.5 px-2 text-sm text-gray-700 focus:outline-none focus:border-whatsapp-green"
            >
              <option value="desc">Descending</option>
              <option value="asc">Ascending</option>
            </select>
          </div>
        </div>

        {isLoading ? (
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-whatsapp-green"></div>
          </div>
        ) : campaigns?.length === 0 ? (
          <div className="bg-white shadow-md rounded-lg p-10 text-center">
            <svg className="w-16 h-16 mx-auto text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
            </svg>
            <h2 className="text-xl text-gray-600 mb-2">No campaigns yet</h2>
            <p className="text-gray-500 mb-4">Create your first WhatsApp campaign to start reaching your audience</p>
            <button
              onClick={openNewCampaignForm}
              className="bg-whatsapp-green hover:bg-whatsapp-dark-green text-white px-4 py-2 rounded-md"
            >
              Create Campaign
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-6">
            {campaigns?.map((campaign: any) => {
              const metrics = getCampaignMetrics(campaign);
              
              return (
                <div key={campaign.id} className="bg-white rounded-lg shadow-md overflow-hidden">
                  <div className="p-6">
                    <div className="flex justify-between items-start">
                      <div>
                        <div className="flex items-center">
                          <h3 className="text-xl font-semibold text-gray-900 mr-3">{campaign.name}</h3>
                          {getCampaignStatusBadge(campaign.status)}
                        </div>
                        <p className="text-gray-600 mt-1">{campaign.description}</p>
                      </div>
                      <div className="flex space-x-2">
                        {campaign.status === 'draft' && (
                          <button
                            onClick={() => handleSchedule(campaign)}
                            className="text-blue-600 hover:text-blue-800 bg-blue-50 hover:bg-blue-100 rounded-md px-3 py-1 text-sm font-medium relative"
                            disabled={isScheduling}
                          >
                            {isScheduling ? (
                              <>
                                <span className="opacity-0">Schedule</span>
                                <span className="absolute inset-0 flex items-center justify-center">
                                  <svg className="animate-spin h-4 w-4 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                  </svg>
                                </span>
                              </>
                            ) : (
                              'Schedule'
                            )}
                          </button>
                        )}
                        {campaign.status === 'scheduled' && (
                          <button
                            onClick={() => handlePauseCampaign(campaign)}
                            className="text-orange-600 hover:text-orange-800 bg-orange-50 hover:bg-orange-100 rounded-md px-3 py-1 text-sm font-medium relative"
                            disabled={isPausing}
                          >
                            {isPausing ? (
                              <>
                                <span className="opacity-0">Pause</span>
                                <span className="absolute inset-0 flex items-center justify-center">
                                  <svg className="animate-spin h-4 w-4 text-orange-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                  </svg>
                                </span>
                              </>
                            ) : (
                              'Pause'
                            )}
                          </button>
                        )}
                        {campaign.status === 'cancelled' && (
                          <button
                            onClick={() => handleResume(campaign)}
                            className="text-green-600 hover:text-green-800 bg-green-50 hover:bg-green-100 rounded-md px-3 py-1 text-sm font-medium relative"
                            disabled={isResuming}
                          >
                            {isResuming ? (
                              <>
                                <span className="opacity-0">Resume</span>
                                <span className="absolute inset-0 flex items-center justify-center">
                                  <svg className="animate-spin h-4 w-4 text-green-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                  </svg>
                                </span>
                              </>
                            ) : (
                              'Resume'
                            )}
                          </button>
                        )}
                        <button
                          onClick={() => editCampaign(campaign.id)}
                          className="text-gray-600 hover:text-gray-800 bg-gray-50 hover:bg-gray-100 rounded-md px-3 py-1 text-sm font-medium"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleDelete(campaign)}
                          className="text-red-600 hover:text-red-800 bg-red-50 hover:bg-red-100 rounded-md px-3 py-1 text-sm font-medium"
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                    
                    <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div className="bg-gray-50 p-3 rounded-lg">
                        <p className="text-sm text-gray-500 mb-1">Template</p>
                        <p className="font-medium">{campaign.template?.name || 'No template selected'}</p>
                      </div>
                      <div className="bg-gray-50 p-3 rounded-lg">
                        <p className="text-sm text-gray-500 mb-1">Scheduled for</p>
                        <p className="font-medium">
                          {campaign.scheduledAt ? formatDate(campaign.scheduledAt) : 'Not scheduled'}
                        </p>
                      </div>
                      <div className="bg-gray-50 p-3 rounded-lg">
                        <p className="text-sm text-gray-500 mb-1">Audience</p>
                        <p className="font-medium">{metrics.totalContacts} contacts</p>
                      </div>
                    </div>
                    
                    {/* Campaign Progress */}
                    {(campaign.status === 'sending' || campaign.status === 'completed') && (
                      <div className="mt-6">
                        <h4 className="text-sm font-medium text-gray-700 mb-2">Campaign Progress</h4>
                        <div className="space-y-3">
                          <div>
                            <div className="flex items-center justify-between text-xs mb-1">
                              <span>Sent</span>
                              <span>{metrics.sentCount}/{metrics.totalContacts} ({Math.round(metrics.sentPercentage)}%)</span>
                            </div>
                            <div className="w-full bg-gray-200 rounded-full h-2">
                              <div className="bg-blue-600 h-2 rounded-full" style={{ width: `${metrics.sentPercentage}%` }}></div>
                            </div>
                          </div>
                          
                          <div>
                            <div className="flex items-center justify-between text-xs mb-1">
                              <span>Delivered</span>
                              <span>{metrics.deliveredCount}/{metrics.sentCount} ({Math.round(metrics.deliveredPercentage)}%)</span>
                            </div>
                            <div className="w-full bg-gray-200 rounded-full h-2">
                              <div className="bg-green-500 h-2 rounded-full" style={{ width: `${metrics.deliveredPercentage}%` }}></div>
                            </div>
                          </div>
                          
                          <div>
                            <div className="flex items-center justify-between text-xs mb-1">
                              <span>Read</span>
                              <span>{metrics.readCount}/{metrics.deliveredCount} ({Math.round(metrics.readPercentage)}%)</span>
                            </div>
                            <div className="w-full bg-gray-200 rounded-full h-2">
                              <div className="bg-yellow-500 h-2 rounded-full" style={{ width: `${metrics.readPercentage}%` }}></div>
                            </div>
                          </div>

                          {metrics.failedCount > 0 && (
                            <div>
                              <div className="flex items-center justify-between text-xs mb-1">
                                <span>Failed</span>
                                <span>{metrics.failedCount}/{metrics.sentCount} ({Math.round(metrics.failedPercentage)}%)</span>
                              </div>
                              <div className="w-full bg-gray-200 rounded-full h-2">
                                <div className="bg-red-500 h-2 rounded-full" style={{ width: `${metrics.failedPercentage}%` }}></div>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                    
                    <div className="mt-6 flex justify-end">
                      <Link 
                        to={`/campaign/${campaign.id}/analytics`}
                        className="text-whatsapp-green hover:text-whatsapp-dark-green text-sm font-medium"
                      >
                        View detailed analytics →
                      </Link>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Campaign Form Modal */}
        {isModalOpen && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg w-full max-w-xl max-h-[90vh] overflow-y-auto">
              <div className="flex justify-between items-center p-4 border-b">
                <h2 className="text-lg font-medium text-gray-900">
                  {currentCampaign ? 'Edit Campaign' : 'Create New Campaign'}
                </h2>
                <button onClick={() => setIsModalOpen(false)} className="text-gray-500 hover:text-gray-700">
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <form onSubmit={handleSubmit(onSubmit)} className="p-4">
                <div className="mb-4">
                  <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="name">
                    Campaign Name *
                  </label>
                  <input
                    type="text"
                    id="name"
                    className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline focus:border-whatsapp-green"
                    placeholder="e.g., September Promotion"
                    {...register("name", { required: "Campaign name is required" })}
                  />
                  {errors.name && <p className="text-red-500 text-xs mt-1">{errors.name.message}</p>}
                </div>

                <div className="mb-4">
                  <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="description">
                    Description
                  </label>
                  <textarea
                    id="description"
                    className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline focus:border-whatsapp-green h-24"
                    placeholder="Describe the purpose of this campaign..."
                    {...register("description")}
                  />
                </div>

                <div className="mb-4">
                  <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="templateId">
                    Message Template *
                  </label>
                  <select
                    id="templateId"
                    className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline focus:border-whatsapp-green"
                    {...register("templateId", { required: "Template selection is required" })}
                  >
                    <option value="">Select a template</option>
                    {templates?.map((template: any) => (
                      <option key={template.id} value={template.id}>
                        {template.name}
                      </option>
                    ))}
                  </select>
                  {errors.templateId && <p className="text-red-500 text-xs mt-1">{errors.templateId.message}</p>}
                  <p className="text-gray-500 text-xs mt-1">
                    Select the message template that will be sent to contacts
                  </p>
                </div>

                <div className="mb-4">
                  <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="contactGroupIds">
                    Target Audience *
                  </label>
                  
                  {/* Hidden field for form submission */}
                  <Controller
                    name="contactGroupIds"
                    control={control}
                    rules={{ 
                      required: "You must select at least one recipient",
                      validate: value => value.length > 0 || "You must select at least one recipient"
                    }}
                    render={({ field }) => (
                      <input 
                        type="hidden" 
                        {...field}
                      />
                    )}
                  />
                  
                  <div className="border rounded-lg overflow-hidden shadow-sm">
                    {/* Search and filter header */}
                    <div className="p-3 border-b bg-gray-50 flex items-center space-x-2">
                      <div className="relative flex-grow">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                          <svg className="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                          </svg>
                        </div>
                        <input
                          type="text"
                          className="w-full pl-10 pr-4 py-2 border-gray-200 rounded-md text-sm focus:border-whatsapp-green focus:ring-whatsapp-green"
                          placeholder="Search by name or phone"
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                        />
                      </div>
                      {(selectedContacts.length > 0 || selectedGroups.length > 0) && (
                        <button
                          type="button"
                          onClick={clearAllSelections}
                          className="text-sm text-red-600 hover:text-red-800"
                        >
                          Clear all
                        </button>
                      )}
                    </div>
                    
                    {/* Tab selector for Contacts/Groups */}
                    <div className="flex border-b">
                      <button
                        type="button"
                        onClick={() => setSelectedTab('contacts')}
                        className={`flex-1 py-2 px-4 text-center text-sm font-medium ${selectedTab === 'contacts' 
                          ? 'border-b-2 border-whatsapp-green text-whatsapp-green' 
                          : 'text-gray-500 hover:text-gray-700'}`}
                      >
                        Contacts
                      </button>
                      <button
                        type="button"
                        onClick={() => setSelectedTab('groups')}
                        className={`flex-1 py-2 px-4 text-center text-sm font-medium ${selectedTab === 'groups' 
                          ? 'border-b-2 border-whatsapp-green text-whatsapp-green' 
                          : 'text-gray-500 hover:text-gray-700'}`}
                      >
                        Groups
                      </button>
                    </div>
                    
                    {/* Contacts/Groups list */}
                    <div className="overflow-y-auto max-h-64">
                      {selectedTab === 'contacts' ? (
                        <div className="divide-y">
                          {filteredContacts.length === 0 ? (
                            <div className="p-4 text-center text-gray-500">
                              {searchTerm ? 'No contacts match your search' : 'No contacts available'}
                            </div>
                          ) : (
                            filteredContacts.map((contact: any) => (
                              <div 
                                key={contact.id} 
                                className={`flex items-center p-3 hover:bg-gray-50 cursor-pointer ${
                                  isContactSelected(contact.id) ? 'bg-blue-50' : ''
                                }`}
                                onClick={() => toggleContactSelection(contact.id)}
                              >
                                <div className="flex-grow">
                                  <div className="font-medium text-gray-900">{contact.name}</div>
                                  <div className="text-sm text-gray-500">{contact.phone}</div>
                                </div>
                                <div>
                                  <input 
                                    type="checkbox" 
                                    checked={isContactSelected(contact.id)}
                                    onChange={() => {}} // Handled by the div onClick
                                    className="h-5 w-5 text-whatsapp-green focus:ring-whatsapp-green rounded"
                                  />
                                </div>
                              </div>
                            ))
                          )}
                        </div>
                      ) : (
                        <div className="divide-y">
                          {filteredGroups.length === 0 ? (
                            <div className="p-4 text-center text-gray-500">
                              {searchTerm ? 'No groups match your search' : 'No groups available'}
                            </div>
                          ) : (
                            filteredGroups.map((group: any) => (
                              <div 
                                key={group.id} 
                                className={`flex items-center p-3 hover:bg-gray-50 cursor-pointer ${
                                  isGroupSelected(group.id) ? 'bg-blue-50' : ''
                                }`}
                                onClick={() => toggleGroupSelection(group.id)}
                              >
                                <div className="flex-grow">
                                  <div className="font-medium text-gray-900">{group.name}</div>
                                  <div className="text-sm text-gray-500">
                                    {group.memberCount || 0} contacts
                                  </div>
                                </div>
                                <div>
                                  <input 
                                    type="checkbox" 
                                    checked={isGroupSelected(group.id)}
                                    onChange={() => {}} // Handled by the div onClick
                                    className="h-5 w-5 text-whatsapp-green focus:ring-whatsapp-green rounded"
                                  />
                                </div>
                              </div>
                            ))
                          )}
                        </div>
                      )}
                    </div>
                    
                    {/* Selected count footer */}
                    <div className="p-3 border-t bg-gray-50 flex items-center justify-between">
                      <div className="text-sm">
                        <span className="font-medium">{recipientCount}</span> recipients selected
                      </div>
                      <div className="text-sm">
                        <span className="font-medium">{selectedContacts.length}</span> contacts, <span className="font-medium">{selectedGroups.length}</span> groups
                      </div>
                    </div>
                  </div>
                  
                  {errors.contactGroupIds && (
                    <p className="text-red-500 text-xs mt-1">{errors.contactGroupIds.message}</p>
                  )}
                   <p className="text-gray-500 text-xs mt-1">
                     Select the contacts or groups who will receive this campaign
                   </p>
                 </div>

                 {/* Cost Estimation Section */}
                 {campaignCost && (
                   <div className="mb-4">
                     <label className="block text-gray-700 text-sm font-bold mb-2">
                       Estimated Cost
                     </label>
                     <div className="border rounded-lg p-4 bg-blue-50">
                       <div className="flex items-center justify-between mb-3">
                         <span className="text-sm font-medium text-gray-700">Total Estimated Cost:</span>
                         <span className={`text-lg font-bold ${campaignCost.totalCost > 50 ? 'text-red-600' : 'text-green-600'}`}>
                           ${campaignCost.totalCost.toFixed(2)}
                         </span>
                       </div>



                       {/* Cost breakdown by country */}
                       <div className="border-t pt-3">
                         <h4 className="text-sm font-medium text-gray-700 mb-2">Cost Breakdown by Country:</h4>
                         <div className="space-y-1">
                           {Object.entries(campaignCost.breakdown).map(([countryCode, data]) => (
                             <div key={countryCode} className="flex justify-between text-xs">
                               <span className="text-gray-600">
                                 {countryCode === 'default' ? 'Other countries' : `Country +${countryCode}`}
                               </span>
                               <span className="text-gray-800">
                                 {data.count} contacts × ${(WHATSAPP_PRICING[countryCode] || WHATSAPP_PRICING.default).toFixed(4)} = ${data.cost.toFixed(2)}
                               </span>
                             </div>
                           ))}
                         </div>
                       </div>

                       {/* Cost warning for high costs */}
                       {campaignCost.totalCost > 50 && (
                         <div className="mt-3 p-2 bg-yellow-100 border border-yellow-300 rounded text-xs text-yellow-800">
                           <strong>High cost alert:</strong> This campaign may incur significant charges. Consider segmenting your audience or using templates to reduce costs.
                         </div>
                       )}
                     </div>
                      <p className="text-gray-500 text-xs mt-1">
                        Cost estimates are based on WhatsApp Business API pricing per conversation and may vary slightly
                      </p>
                   </div>
                 )}

                 <div className="mb-4">
                   <label className="block text-gray-700 text-sm font-bold mb-2">
                     Schedule
                   </label>
                  
                  {/* Scheduling options */}
                  <div className="mb-2">
                    <div className="flex items-center mb-2">
                      <input
                        id="one-time"
                        type="radio"
                        name="schedule-type"
                        className="mr-2"
                        checked={!isRecurring}
                        onChange={() => {
                          setIsRecurring(false);
                          setShowRecurringOptions(false);
                        }}
                      />
                      <label htmlFor="one-time" className="text-sm text-gray-700">One-time campaign</label>
                    </div>
                    
                    <div className="flex items-center mb-2">
                      <input
                        id="recurring"
                        type="radio"
                        name="schedule-type"
                        className="mr-2"
                        checked={isRecurring}
                        onChange={() => {
                          setIsRecurring(true);
                          setShowRecurringOptions(true);
                        }}
                      />
                      <label htmlFor="recurring" className="text-sm text-gray-700">Recurring campaign</label>
                    </div>
                  </div>
                  
                  {!isRecurring ? (
                    // One-time schedule
                    <div>
                      <input
                        type="datetime-local"
                        className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline focus:border-whatsapp-green"
                        {...register("scheduledAt")}
                      />
                      <p className="text-gray-500 text-xs mt-1">
                        Leave blank to save as draft without scheduling
                      </p>
                      <p className="text-gray-500 text-xs mt-1">
                        Using your local timezone ({Intl.DateTimeFormat().resolvedOptions().timeZone})
                      </p>
                    </div>
                  ) : (
                    // Recurring schedule options
                    <div className="border rounded-md p-4 bg-gray-50">
                      <div className="mb-3">
                        <label className="block text-gray-700 text-sm font-medium mb-2">Recurrence</label>
                        <select
                          className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline focus:border-whatsapp-green"
                          value={recurrenceType}
                          onChange={(e) => setRecurrenceType(e.target.value)}
                        >
                          <option value="daily">Daily</option>
                          <option value="weekly">Weekly</option>
                          <option value="monthly">Monthly</option>
                        </select>
                      </div>
                      
                      {/* Show day selection for weekly recurrence */}
                      {recurrenceType === 'weekly' && (
                        <div className="mb-3">
                          <label className="block text-gray-700 text-sm font-medium mb-2">Days of week</label>
                          <div className="grid grid-cols-7 gap-1">
                            {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day, index) => (
                              <div key={day} className="flex flex-col items-center">
                                <input
                                  type="checkbox"
                                  id={`day-${index}`}
                                  checked={recurrenceDays.includes(index)}
                                  onChange={() => {
                                    const newDays = recurrenceDays.includes(index)
                                      ? recurrenceDays.filter(d => d !== index)
                                      : [...recurrenceDays, index];
                                    setRecurrenceDays(newDays);
                                  }}
                                  className="mb-1"
                                />
                                <label htmlFor={`day-${index}`} className="text-xs">{day}</label>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                      
                      {/* Show day selection for monthly recurrence */}
                      {recurrenceType === 'monthly' && (
                        <div className="mb-3">
                          <label className="block text-gray-700 text-sm font-medium mb-2">Days of month</label>
                          <div className="flex flex-wrap gap-1">
                            <select
                              multiple
                              size={5}
                              className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline focus:border-whatsapp-green"
                              value={recurrenceDays.map(String)}
                              onChange={(e) => {
                                const selectedDays = Array.from(e.target.selectedOptions).map(opt => parseInt(opt.value));
                                setRecurrenceDays(selectedDays);
                              }}
                            >
                              {Array.from({ length: 31 }, (_, i) => i + 1).map(day => (
                                <option key={day} value={day}>{day}</option>
                              ))}
                            </select>
                            <p className="text-gray-500 text-xs">
                              Hold Ctrl/Cmd to select multiple days
                            </p>
                          </div>
                        </div>
                      )}
                      
                      <div className="mb-3">
                        <label className="block text-gray-700 text-sm font-medium mb-2">Time ({Intl.DateTimeFormat().resolvedOptions().timeZone})</label>
                        <div className="flex space-x-2">
                          <select
                            className="shadow appearance-none border rounded w-1/2 py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                            value={recurrenceHour}
                            onChange={(e) => setRecurrenceHour(parseInt(e.target.value))}
                          >
                            {Array.from({ length: 24 }, (_, i) => i).map(hour => (
                              <option key={hour} value={hour}>
                                {hour.toString().padStart(2, '0')}
                              </option>
                            ))}
                          </select>
                          <span className="flex items-center">:</span>
                          <select
                            className="shadow appearance-none border rounded w-1/2 py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                            value={recurrenceMinute}
                            onChange={(e) => setRecurrenceMinute(parseInt(e.target.value))}
                          >
                            {[0, 5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 55].map(minute => (
                              <option key={minute} value={minute}>
                                {minute.toString().padStart(2, '0')}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>
                      
                      <div>
                        <label className="block text-gray-700 text-sm font-medium mb-2">End date (optional)</label>
                        <input
                          type="date"
                          className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline focus:border-whatsapp-green"
                          {...register("recurrenceEndDate")}
                        />
                        <p className="text-gray-500 text-xs mt-1">
                          Leave blank to run indefinitely
                        </p>
                      </div>
                    </div>
                  )}
                </div>

                <div className="flex justify-end">
                  <button
                    type="button"
                    onClick={() => setIsModalOpen(false)}
                    className="bg-white hover:bg-gray-100 text-gray-700 border border-gray-300 font-semibold py-2 px-4 rounded mr-2"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={isCreating || isUpdating}
                    className="bg-whatsapp-green hover:bg-whatsapp-dark-green text-white font-semibold py-2 px-4 rounded flex items-center"
                  >
                    {(isCreating || isUpdating) && (
                      <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                    )}
                    {currentCampaign ? 'Update Campaign' : 'Create Campaign'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Delete Confirmation Modal */}
        <ConfirmModal
          isOpen={isDeleteModalOpen}
          title="Delete Campaign"
          message={
            <>
              Are you sure you want to delete the campaign
              {campaignToDelete ? (
                <> <span className="font-semibold">"{campaignToDelete.name}"</span>? This action cannot be undone.</>
              ) : (
                <>? This action cannot be undone.</>
              )}
            </>
          }
          confirmText="Delete"
          cancelText="Cancel"
          destructive
          loading={isDeletingConfirm}
          onConfirm={confirmDeleteCampaign}
          onCancel={() => { setIsDeleteModalOpen(false); setCampaignToDelete(null); }}
        />
      </div>
    </MainLayout>
  );
};

export default Campaigns;
